import { ErrorMessage, Field, Form, Formik } from "formik";
import { ReactElement, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import ic_default from "../../../assets/icons/ic_user_default.svg";
import { ButtonCustom } from "../../../components/custom/button-custom";
import ImageCustom from "../../../components/custom/image-custom";
import IconCamera from "../../../components/icons/icon-camera";
import { IconEllipse } from "../../../components/icons/icon-group";
import { GENDER, PAGE_SIZES, STATUS } from "../../../constants/status";
import { STRING_MENU } from "../../../constants/string";
import { MODULE_ROUTE } from "../../../routers/module-router";
import {
  setSidebarMenu,
  setSidebarMenuSub,
  setSidebarThreeMenuSub,
  toggleMenuContent,
} from "../../../store/themeConfigSlice";

import { HttpStatusCode } from "axios";
import DateCustomInput from "../../../components/custom/date-input/date-input-custom";
import { SelectDropdown } from "../../../components/custom/select-dropdown";
import SelectDropdownAsyncPaginate from "../../../components/custom/select-dropdown-async-paginate";
import { usePageTitle } from "../../../hooks/usePageTitle";
import { useDataResponse } from "../../../hooks/useResponseHandler";
import { User as UserRequest } from "../../../models/request/user";
import { PrivilegesGroup } from "../../../models/response/user/privileges-group";
import { User } from "../../../models/response/user/user";

import {
  Group,
  LoadingOverlay,
  MantineProvider,
  Radio,
  Switch,
} from "@mantine/core";
import { showToast } from "../../../components/custom/custom-toast";
import { API_EMPLOYEE_ROUTER } from "../../../services/api-config";
import CookieUtils from "../../../utils/cookie-utils";
import { getTodayDate } from "../../../utils/time-utils";
import { Department } from "../../department/services/department-service";
import { MediaService } from "../services/auth-service";
import { EmployeeService } from "../services/employee-service";

const ProfilePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  usePageTitle(STRING_MENU.PROFILE);
  useEffect(() => {
    dispatch(toggleMenuContent(STRING_MENU.PROFILE));
    dispatch(setSidebarMenu(""));
    dispatch(setSidebarMenuSub(""));
    dispatch(setSidebarThreeMenuSub(""));
  }, []);

  const [isImageLocal, setIsImageLocal] = useState(false);
  const [imageDisplay, setImageDisplay] = useState("");
  const [imageUpload, setImageUpload] = useState<FileList | null>(null);

  const [profileData, setProfileData] = useState<User>(new User());
  const [profileRequest, setProfileRequest] = useState<UserRequest>(
    new UserRequest()
  );
  const [is2FA, setIs2FA] = useState<number>(0);

  const [departments, setDepartments] = useState<Department[]>([]);
  const [privileges, setPrivileges] = useState<PrivilegesGroup[]>([]);
  const [dataSalaryLevels, setDataSalaryLevels] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const [urlQR, setUrlQR] = useState<string>("");
  const [isLoadingQR, setIsLoadingQR] = useState<boolean>(false);

  const {
    fetch: onGetProfile,
    response: responsesProfile,
    loading,
  } = EmployeeService.getEmployeeDetail();
  const {
    uploadFiles: onUploadFile,
    baseResponse: responseUpload,
    isUploading: loadingUpload,
  } = MediaService.Upload();
  const {
    fetch: onUpdateProfile,
    response: responsesUpdateProfile,
    loading: loadingSave,
  } = EmployeeService.updateEmployee();

  const {
    fetch: onUpdate2Fa,
    response: responsesUpdate2FA,
    loading: loading2FA,
  } = EmployeeService.update2fa();

  useEffect(() => {
    onGetProfile((CookieUtils.getCurrentUser().id ?? 0).toString());
  }, []);

  useDataResponse(responsesProfile, (data) => {
    setProfileData(data);
    setIsImageLocal(data.avatar === "");
    setImageDisplay(data.avatar);
    setIs2FA(data.is_2fa); // giữ nguyên 0/1

    if (data.totp_qr_code.includes("data:image")) {
      setUrlQR(data.totp_qr_code);
    } else {
      handleQRCode(data.totp_qr_code);
    }
  });

  const handleQRCode = (QRStringCode: string) => {
    setIsLoadingQR(true);
    const url = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
      QRStringCode
    )}`;
    fetch(url)
      .then((res) => {
        if (res.status === HttpStatusCode.Ok) {
          console.log("QR URL: ", url);
          setUrlQR(url);
        } else {
          setUrlQR("");
        }
        setIsLoadingQR(false);
      })
      .catch((err) => {
        console.error(err);
        setUrlQR("");
        setIsLoadingQR(false);
      });
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files; // Lấy tệp hình ảnh
    if (files && files.length > 0) {
      setIsImageLocal(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageDisplay(reader.result as string); // Cập nhật URL hình ảnh
      };
      reader.readAsDataURL(files[0]); // Đọc tệp dưới dạng URL
      setImageUpload(files);
    }
  };
  useEffect(() => {
    if (!responseUpload) {
      return;
    }
    if (
      responseUpload.status === HttpStatusCode.Ok &&
      responseUpload.data.length > 0
    ) {
      onUpdateProfile((CookieUtils.getCurrentUser().id ?? 0).toString(), {
        name: profileRequest.name,
        username: profileRequest.username,
        birthday: profileRequest.birthday,
        joining_date: profileRequest.joining_date,
        gender: profileRequest.gender,
        avatar: responseUpload.data[0].path,
        phone: profileRequest.phone,
        address: profileRequest.address,
        id_card_number: profileRequest.id_card_number,
        department_id: profileRequest.department_id,
        manager_id: profileRequest.manager_id,
        privilege_group_id: profileRequest.privilege_group_id,
        email: profileRequest.email,
      });
    } else {
      showToast({
        message: "Có lỗi xảy ra vui lòng thử lại sau",
        type: "warning",
      });
    }
  }, [responseUpload]);

  const handleUpdateProfile = (values: User) => {
    if (imageUpload) {
      onUploadFile(imageUpload);
      setProfileRequest(values);
    } else {
      onUpdateProfile(values.id.toString(), {
        name: values.name,
        username: values.username,
        birthday: values.birthday,
        joining_date: values.joining_date,
        gender: values.gender,
        avatar: profileData.avatar,
        phone: values.phone,
        address: values.address,
        id_card_number: values.id_card_number,
        department_id: values.department_id,
        manager_id: values.manager_id,
        privilege_group_id: values.privilege_group_id,
        email: values.email,
      });
    }
  };
  useEffect(() => {
    if (!responsesUpdateProfile) {
      return;
    }
    if (responsesUpdateProfile.status === HttpStatusCode.Ok) {
      const response = responsesUpdateProfile.data;
      CookieUtils.setCookie(
        "user",
        JSON.stringify({
          ...CookieUtils.getCurrentUser(),
          name: response.name,
          gender: response.gender,
          email: response.email,
          avatar: response.avatar,
          birthday: response.birthday,
          phone: response.phone,
          id_card_number: response.id_card_number,
          address: response.address,
        })
      );
      showToast({ message: "Chỉnh sửa thành công!", type: "success" });
    } else {
      showToast({ message: responsesUpdateProfile.message, type: "warning" });
    }
  }, [responsesUpdateProfile]);
  const handleToggle = async (checked: boolean) => {
    if (isProcessing) return;
    setIsProcessing(true);

    const prevValue = is2FA;
    const newValue = checked ? 1 : 0;

    // Cập nhật UI ngay để phản hồi nhanh
    setIs2FA(newValue);

    try {
      // Gọi API cập nhật
      const res = await onUpdate2Fa();

      if (res?.status === HttpStatusCode.Ok) {
        showToast({ message: "Cập nhật 2FA thành công", type: "success" });
      } else {
        // Rollback nếu API trả lỗi logic
        setIs2FA(prevValue);
        showToast({
          message: res?.message || "Không thể cập nhật 2FA",
          type: "warning",
        });
      }
    } catch (error) {
      // Rollback nếu có lỗi mạng hoặc lỗi khác
      setIs2FA(prevValue);
      showToast({
        message: "Lỗi hệ thống khi cập nhật 2FA, vui lòng thử lại.",
        type: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  function renderField({
    label,
    name,
    field,
    isFullRow = false,
    required = false,
  }: {
    label: string;
    name: string;
    field: ReactElement<any, any>;
    isFullRow?: boolean;
    required?: boolean;
  }) {
    return (
      <div className={`w-full ${isFullRow ? "col-span-full" : ""}`}>
        <label htmlFor={name}>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
        {field}
        <ErrorMessage
          name={name}
          component="div"
          className="mt-1 text-xs text-red-500"
        />
      </div>
    );
  }

  const SubmittedForm = Yup.object().shape({
    name: Yup.string()
      .min(2, "Độ dài tối thiểu 2 ký tự")
      .max(50, "Độ dài tối đa 50 ký tự")
      .required("Độ dài tối thiểu 2 ký tự"),
    phone: Yup.string()
      .matches(
        /^0/,
        "Số điện thoại chưa đúng định dang, số đầu tiên phải là số 0"
      )
      .min(10, "Số điện thoại chưa đúng 10 hoặc 11 số"),
    id_card_number: Yup.string()
      .min(9, "Độ dài tối thiểu 9 ký tự")
      .max(12, "Độ dài tối đa 12 ký tự"),
    birthday: Yup.string().required("Vui lòng điền thông tin"),
  });

  return (
    <div className="relative self-center p-4 mx-auto w-full bg-white rounded-xl shadow-2xl h-fit dark:bg-gray-800/40">
      <LoadingOverlay
        visible={loading}
        zIndex={1000}
        radius="sm" // Truyền trực tiếp radius
        overlayBlur={1}
      />
      <div className="flex gap-4 justify-center items-center w-full h-44 mb-4">
        <div className="relative">
          <ImageCustom
            url={`${imageDisplay || ic_default}`}
            className="w-[150px] h-[150px] rounded-full object-cover"
            isLocal={isImageLocal}
            data-testid="image-profile"
          />
          <IconEllipse className="absolute inset-0 flex items-center justify-center w-[150px] h-[150px]" />
          <div className="absolute right-0 bottom-0 p-2 rounded-full border border-white cursor-pointer bg-black/50">
            <input
              type="file"
              name="profile"
              accept="image/*"
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={handleImageChange}
              data-testid="input-profile-file"
              title=""
            />
            <IconCamera
              duotone={false}
              fill
              color="white"
              className="cursor-pointer"
            />
          </div>
        </div>
        <div className="flex flex-col">
          <div className="shadow-2xl relative bg-white border rounded-lg w-[9.375rem] h-[9.375rem] overflow-hidden">
            <LoadingOverlay
              visible={isLoadingQR}
              zIndex={1000}
              radius="sm" // Truyền trực tiếp radius
              overlayBlur={1}
            />
            <ImageCustom
              isLocal={urlQR.includes("data:image")}
              isUser={false}
              url={urlQR}
              className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${
                urlQR.includes("data:image") ? "" : "p-2"
              }`}
            />
          </div>
        </div>
      </div>
      <Formik
        enableReinitialize
        initialValues={profileData}
        validationSchema={SubmittedForm}
        onSubmit={handleUpdateProfile}
      >
        {({ values, submitForm, setFieldValue }) => (
          <>
            <Form className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {renderField({
                required: true,
                label: "Họ và tên",
                name: "name",
                field: (
                  <Field
                    name="name"
                    type="text"
                    placeholder="Nhập họ và tên"
                    className="form-input"
                    value={values.name}
                  />
                ),
              })}
              {renderField({
                label: "Tên đăng nhập",
                name: "username",
                field: (
                  <Field
                    disabled
                    autoComplete="off"
                    name="username"
                    type="text"
                    placeholder="Nhập tên đăng nhập"
                    className="form-input disabled:cursor-not-allowed disabled:bg-[#eee] dark:disabled:bg-[#1b2e4b]"
                    value={values.username}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue("username", e.target.value);
                    }}
                  />
                ),
              })}
              <div className="w-full">
                <label htmlFor="gender">Giới tính</label>
                <Radio.Group
                  withAsterisk
                  name="salaryOption"
                  value={values.gender.toString()}
                  onChange={(value: string) => {
                    const isMale = Number(value) === GENDER.MALE;
                    setFieldValue(
                      "gender",
                      isMale ? GENDER.MALE : GENDER.FEMALE
                    );
                  }}
                >
                  <Group mt="xs">
                    <Radio value={`${GENDER.MALE}`} label="Nam" />
                    <Radio value={`${GENDER.FEMALE}`} label="Nữ" />
                  </Group>
                </Radio.Group>
              </div>
              {renderField({
                label: "Email",
                name: "email",
                field: (
                  <Field
                    autoComplete="off"
                    name="email"
                    type="text"
                    placeholder="Nhập địa chỉ email"
                    className="form-input"
                    value={values.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue("email", e.target.value);
                    }}
                  />
                ),
              })}
              {renderField({
                required: true,
                label: "Ngày sinh",
                name: "birthday",
                field: (
                  <Field name="birthday">
                    {() => (
                      <DateCustomInput
                        isShowClearDate
                        maxDate={getTodayDate()}
                        placeholder="Ngày sinh"
                        value={values.birthday}
                        onChange={(date: string) => {
                          setFieldValue("birthday", date);
                        }}
                      />
                    )}
                  </Field>
                ),
              })}
              {renderField({
                label: "Ngày vào làm việc",
                name: "joining_date",
                field: (
                  <Field name="joining_date">
                    {() => (
                      <DateCustomInput
                        disabled
                        placeholder="Ngày vào làm việc"
                        value={values.joining_date}
                        onChange={(date: string) => {
                          setFieldValue("joining_date", date);
                        }}
                      />
                    )}
                  </Field>
                ),
              })}
              {renderField({
                required: true,
                label: "Số điện thoại",
                name: "phone",
                field: (
                  <Field
                    name="phone"
                    maxLength={11}
                    type="text"
                    placeholder="Nhập số điện thoại"
                    className="form-input"
                    value={values.phone}
                  />
                ),
              })}
              {renderField({
                label: "CCCD",
                name: "id_card_number",
                field: (
                  <Field
                    name="id_card_number"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Nhập CCCD"
                    className="form-input"
                    value={values.id_card_number}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
                      setFieldValue("id_card_number", value);
                    }}
                  />
                ),
              })}

              <div className="w-full cursor-not-allowed">
                {renderField({
                  label: "Bộ phận",
                  name: "department_id",
                  field: (
                    <Field name="department_id" className="form-select">
                      {() => (
                        <SelectDropdown
                          disabled
                          selectedId={values.department_id}
                          placeholder="Chọn bộ phận"
                          dataOptions={departments}
                          getOptionLabel={(option) => option.name}
                          getOptionKey={(option) => option.id}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              "department_id",
                              selectedOption?.id ?? ""
                            );
                          }}
                        />
                      )}
                    </Field>
                  ),
                })}
              </div>
              <div className="w-full cursor-not-allowed">
                {renderField({
                  label: "Người quản lý",
                  name: "manager_id",
                  field: (
                    <Field name="manager_id" className="form-select">
                      {() => (
                        <SelectDropdownAsyncPaginate
                          disabled
                          url={API_EMPLOYEE_ROUTER.API_GET_EMPLOYEES}
                          limit={PAGE_SIZES[0]}
                          params={{
                            department_id: values.department_id,
                            status: STATUS.ACTIVE,
                          }}
                          placeholder="Chọn người quản lý"
                          onChange={(selectedOption) => {
                            setFieldValue("manager_id", selectedOption.id);
                            setFieldValue("manager_name", selectedOption.name);
                          }}
                          {...(values.manager_id !== 0 && {
                            defaultOptions: {
                              id: values.manager_id,
                              name: values.manager_name,
                            },
                          })}
                        />
                      )}
                    </Field>
                  ),
                })}
              </div>

              <div className="w-full cursor-not-allowed">
                {renderField({
                  label: "Nhóm quyền",
                  name: "privilege_group_id",
                  field: (
                    <Field name="privilege_group_id" className="form-select">
                      {() => (
                        <SelectDropdown
                          disabled
                          selectedId={values.privilege_group_id}
                          placeholder="Chọn nhóm quyền"
                          dataOptions={privileges} // Ensure it's always an array
                          getOptionLabel={(option) => option.name}
                          getOptionKey={(option) => option.id}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              "privilege_group_id",
                              selectedOption?.id ?? 0
                            );
                          }}
                        />
                      )}
                    </Field>
                  ),
                })}
              </div>
              {/* <div className="w-full cursor-not-allowed">
                {renderField({
                  label: "Bậc lương",
                  name: "salary_level_id",
                  field: (
                    <Field name="salary_level_id" className="form-select">
                      {() => (
                        <SelectDropdown
                          disabled
                          selectedId={values.salary_level.id.toString()}
                          placeholder="Chọn bậc lương"
                          dataOptions={dataSalaryLevels} // Ensure it's always an array
                          getOptionLabel={(option) => option.name}
                          getOptionKey={(option) => option.id}
                          onChange={(selectedOption) => {
                            setFieldValue("salary_level_id", selectedOption?.id ?? "");
                          }}
                        />
                      )}
                    </Field>
                  ),
                })}
              </div> */}
              {renderField({
                label: "Địa chỉ",
                name: "address",
                field: (
                  <Field
                    name="address"
                    type="text"
                    maxLength={255}
                    placeholder="Nhập địa chỉ"
                    className="form-input"
                    value={values.address}
                  />
                ),
              })}
            </Form>
            <div className="flex gap-3 justify-end mt-6">
              <ButtonCustom
                type="button"
                text="Đổi mật khẩu"
                className="btn uppercase px-4 py-2 font-bold text-[#E96012] hover:text-white rounded-md bg-[#FEEED6] hover:bg-[#e96012]"
                onClick={() => {
                  navigate(MODULE_ROUTE.CHANGE_PASSWORD);
                }}
              />
              <ButtonCustom type="button" text="Lưu lại" onClick={submitForm} />
            </div>
          </>
        )}
      </Formik>
      <div className="p-4 mt-4 border rounded-lg shadow-inner bg-gray-50 dark:bg-gray-700/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Xác thực 2 bước
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Bảo vệ tài khoản của bạn với xác thực hai yếu tố
            </p>
          </div>
          <div className="relative">
            <MantineProvider>
              <Switch
                checked={is2FA == 1}
                onChange={(e) => handleToggle(e.currentTarget.checked)}
                size="lg"
                color="green"
                className="-mb-[6px]"
              />
            </MantineProvider>
            {loading2FA && (
              <div className="absolute inset-0 flex flex-col items-center justify-center flex-1 bg-white/40 backdrop-blur-sm">
                <div className="relative">
                  <div className="w-5 h-5 border-2 border-indigo-200 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-5 h-5 border-t-2 border-indigo-500 rounded-full animate-spin"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
